/**
 * Firestore snapshot freshness helpers.
 *
 * Use with listeners that pass `{ includeMetadataChanges: true }` so the first
 * emission can come from IndexedDB (persistent local cache) and later emissions
 * confirm server data. Server/listener data always wins over app-level caches.
 */

/** @typedef {'cache' | 'server' | 'pending-local-write' | 'unknown'} FirestoreSyncSource */

/**
 * @typedef {Object} FirestoreSnapshotFreshness
 * @property {FirestoreSyncSource} source
 * @property {boolean} isFromCache
 * @property {boolean} hasPendingWrites
 * @property {number|null} lastServerSyncAt - epoch ms when source is server
 */

/**
 * Derive a small freshness record from Firestore snapshot metadata.
 * @param {import('firebase/firestore').DocumentSnapshot|import('firebase/firestore').QuerySnapshot} snapshot
 * @returns {FirestoreSnapshotFreshness}
 */
export const parseSnapshotMetadata = (snapshot) => {
  const isFromCache = snapshot?.metadata?.fromCache === true;
  const hasPendingWrites = snapshot?.metadata?.hasPendingWrites === true;

  /** @type {FirestoreSyncSource} */
  let source = "unknown";

  if (hasPendingWrites) {
    source = "pending-local-write";
  } else if (isFromCache) {
    source = "cache";
  } else {
    source = "server";
  }

  return {
    source,
    isFromCache,
    hasPendingWrites,
    lastServerSyncAt: source === "server" ? Date.now() : null,
  };
};

/**
 * True when snapshot data is safe to render immediately but may not yet match
 * the server (offline IndexedDB or optimistic local write).
 * @param {FirestoreSnapshotFreshness} freshness
 */
export const isProvisionalSnapshot = (freshness) =>
  freshness?.source === "cache" || freshness?.source === "pending-local-write";

/**
 * True when the snapshot reflects confirmed server state (no pending local writes).
 * @param {FirestoreSnapshotFreshness} freshness
 */
export const isServerConfirmedSnapshot = (freshness) =>
  freshness?.source === "server";
